<template>
  <div
    class="text-white !flex !flex-col !justify-between !bg-cover !bg-no-repeat lg:!w-[340px] md:!w-[340px] w-[300px] h-[230px] p-1 rounded-xl"
    :style="{backgroundImage}"
  >
    <div class="flex flex-col items-end gap-[5px]">
      <div class="flex gap-1">
        <div class="capitalize font-medium text-lg text-end">
          {{ data.name }}
        </div>
        <div
          class="rounded-full px-[10px] py-[3px]"
          :class="['badge', badge]"
        >
          {{ data.level === 'gold' ? 'Gold' : data.level === 'bronze' ? 'Bronze' : 'Platinum' }}
        </div>
      </div>
      <div class="flex items-center gap-3">
        <div
          v-if="data.labels"
          class="flex items-center gap-2"
        >
          <div class="cursor-pointer">
            <img
              v-b-popover.hover.top="data.labels"
              src="https://storage.googleapis.com/komerce/assets/komerce-icon/Putih/note-text.svg"
              alt="note"
              width="17"
            >
          </div>
          <div class="w-[1px] h-[15px] bg-white" />
        </div>
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="6"
            cy="6"
            r="6"
            :fill="renderColorStatus(data.status)"
          />
        </svg>
        {{ renderStatus(data.status) }}
      </div>
    </div>
    <div
      v-if="loading"
      class="skeleton shimmer h-[36px] w-full rounded-md"
    />
    <div
      v-else
      class="text-end text-2xl font-semibold"
    >
      {{ IDR(data.balance, 2, 2) }}
    </div>
    <div class="flex flex-col gap-1">
      <div class="flex flex-col">
        <span class="capitalize text-sm">card number</span>
        <span class="font-semibold text-lg">{{ data.card_number }}</span>
      </div>
      <div class="flex justify-between items-center">
        <div
          v-if="role !== 'TALENT GLOBAL'"
          class="text-[10px] lg:text-sm !py-[2px] !px-[5px] !rounded-full !font-normal"
          :class="['action-button', actionClass]"
          @click="onTopup"
        >
          <i class="k-send-square" /> Top up
        </div>
        <div
          class="text-[10px] lg:text-sm !py-[2px] !px-[5px] !rounded-full !font-normal cursor-pointer"
          :class="['action-button', actionClass]"
          @click="onTransfer"
        >
          <i class="k-convert-card" /> Transfer Saldo
        </div>
        <div
          class="text-[10px] lg:text-sm !py-[2px] !px-[5px] !rounded-full !font-normal cursor-pointer"
          :class="data.level === 'gold' ? 'bg-[#BC9744]' : 'bg-[#6a6a6a]'"
          @click="onDetail"
        >
          <i class="k-card" /> Detail
        </div>
        <div class="w-[1px] h-[20px] bg-white" />
        <img
          src="https://storage.googleapis.com/komerce/assets/elements/Mastercard-logo.svg"
          alt="mastercard"
          width="30"
        >
      </div>
    </div>
  </div>
</template>

<script>
import { IDR } from '@/libs/currency'
import secureLs from '@/libs/secureLs'

export default {
  props: {
    data: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      role: secureLs.getItem('userData').role_name.toUpperCase(),
      IDR,
    }
  },
  computed: {
    theme() {
      const disabledStatuses = new Set(['freeze', 'cancel', 'inactive'])
      const { status = '', level = '' } = this.data || {}

      // URLs centralized so it's easy to change later
      const urls = {
        disabled: 'https://storage.googleapis.com/komerce/Komcards/disable-card.svg',
        gold: 'https://storage.googleapis.com/komerce/assets/media/Komcards-Cards-gold.svg',
        platinum: 'https://storage.googleapis.com/komerce/assets/svg/platinum.svg',
        bronze: 'https://storage.googleapis.com/komerce/assets/elements/Cards-bronze.svg',
      }

      // If disabled status -> always use disabled theme
      if (disabledStatuses.has(status)) {
        return {
          backgroundImage: `url(${urls.disabled})`,
          badge: 'bg-[#626262]',
          actionClass: 'cursor-not-allowed bg-[#414242]',
        }
      }

      // Level mapping for non-disabled states
      const levelMap = {
        gold: {
          backgroundImage: `url(${urls.gold})`,
          badge: 'bg-[#F6B732]',
          actionClass: 'bg-[#BC9744] cursor-pointer',
        },
        platinum: {
          backgroundImage: `url(${urls.platinum})`,
          badge: 'bg-[#414242]',
          actionClass: 'bg-[#626262] cursor-pointer',
        },
        default: {
          backgroundImage: `url(${urls.bronze})`,
          badge: 'bg-[#686868]',
          actionClass: 'bg-[#686868] cursor-pointer',
        },
      }

      return levelMap[level] || levelMap.default
    },

    // Backwards-compatible computed props (optional)
    backgroundImage() {
      return this.theme.backgroundImage
    },
    badge() {
      return this.theme.badge
    },
    actionClass() {
      return this.theme.actionClass
    },
  },
  methods: {
    onTransfer() {
      if (this.data.status !== 'active') return
      this.$emit('handleTransfer', this.data.id)
    },
    onDetail() {
      const card = {
        status: this.data.status,
        id: this.data.id,
      }
      this.$emit('handleDetail', card)
    },
    onTopup() {
      const card = {
        id: this.data.id,
        card_number: this.data.card_number,
        name: this.data.name,
        status: this.data.status,
      }

      if (this.data.level === 'bronze' || this.data.level === 'gold' || ['cancel', 'freeze'].includes(this.data.status)) return
      this.$emit('handleTopup', card)
    },
    renderStatus(status) {
      if (status === 'active') {
        return 'Aktif'
      }
      if (status === 'inactive') {
        return 'Tidak Aktif'
      }
      if (status === 'freeze') {
        return 'Dibekukan'
      }
      if (status === 'cancel') {
        return 'Cancelled'
      }
      return 'Pending'
    },
    renderColorStatus(status) {
      if (status === 'active') {
        return '#34A770'
      }
      if (status === 'inactive') {
        return '#E31A1A'
      }
      if (status === 'freeze') {
        return '#C2C2C2'
      }
      if (status === 'cancel') {
        return '#C2C2C2'
      }
      return '#828282'
    },
  },
}
</script>

<style scoped>
.bronze {
  background: linear-gradient(180deg,#a2a4a5 -57.69%,#7d4208 146.15%) !important;
}

.gold {
  background: linear-gradient(180deg, #DD3F0B 0%, #AA1D00 100%);
}

.skeleton {
  background-color: #e0e0e0;
  position: relative;
  overflow: hidden;
}

.shimmer::after {
  content: '';
  position: absolute;
  top: 0;
  left: -150px;
  height: 100%;
  width: 150px;
  background: linear-gradient(to right, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%);
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    left: -150px;
  }
  100% {
    left: 100%;
  }
}
li::marker {
  font-size: 2em;
}
</style>
